/**
 * Report-side asset labels. Twin-facing survey copy stays the ordinal
 * ("Ad 1"); analysis charts name the asset by its authored title from
 * `assetContext` (`assetCatalogTitle`), the ordinal only when it has none.
 */

import { assetCatalogTitle } from '@digital-twins/shared-types'

export interface AssetReportContextItem {
  assetId: string
  title?: string
  twinFacingLabel?: string
  [key: string]: unknown
}

function slugKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function identityKeys(item: AssetReportContextItem): Array<string> {
  return [item.assetId, item.twinFacingLabel, item.title]
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0)
}

function matchesAsset(item: AssetReportContextItem, candidate: string): boolean {
  const needle = candidate.trim()
  if (!needle) return false
  const needleLower = needle.toLowerCase()
  const needleSlug = slugKey(needle)
  return identityKeys(item).some((key) => {
    return key.toLowerCase() === needleLower || slugKey(key) === needleSlug
  })
}

export function findAssetReportContextItem(
  candidates: Array<string>,
  items?: Array<AssetReportContextItem> | null,
): AssetReportContextItem | null {
  if (!items?.length) return null
  const unique = Array.from(
    new Set(candidates.map((value) => String(value ?? '').trim()).filter(Boolean)),
  )
  for (const candidate of unique) {
    const match = items.find((item) => matchesAsset(item, candidate))
    if (match) return match
  }
  return null
}

/**
 * `Ad 1` with title `Flavor Stacking` → `Flavor Stacking`.
 * Falls back to the raw chart label when the value is not an asset.
 */
export function formatAssetReportLabel(
  raw: string,
  items?: Array<AssetReportContextItem> | null,
  extraCandidates: Array<string> = [],
): string {
  const fallback = String(raw ?? '')
  const item = findAssetReportContextItem([fallback, ...extraCandidates], items)
  if (!item) return fallback
  return assetCatalogTitle({ twinFacingLabel: item.twinFacingLabel, name: item.title }) || fallback
}

/**
 * Reader-facing names for a set of lanes. Duplicate names keep a stable
 * id suffix, matching the deck's `disambiguated_asset_labels`.
 */
export function disambiguatedAssetReportLabels(
  assetIds: Array<string>,
  items?: Array<AssetReportContextItem> | null,
): Record<string, string> {
  const raw: Record<string, string> = {}
  for (const assetId of assetIds) {
    raw[assetId] = formatAssetReportLabel(assetId, items, [assetId])
  }
  const counts: Record<string, number> = {}
  for (const name of Object.values(raw)) {
    counts[name] = (counts[name] || 0) + 1
  }
  const labelled: Record<string, string> = {}
  for (const [assetId, name] of Object.entries(raw)) {
    labelled[assetId] = counts[name] > 1 ? `${name} (${assetId})` : name
  }
  return labelled
}
